import { opportunityService } from '@/services/opportunity.service';
import axios from 'axios';
import { updateRouteQuery } from '@/util/path-util';

const initialState = () => ({
  opportunities: [],
  pagination: {
    page: 1,
    totalCount: 0,
    totalPages: 0,
  },
  sortingOrder: 'desc',
  sortingField: 'created_at',
  filters: {
    authorId: null,
    hasDocuments: null,
    tenderId: null,
    opportunityStatusId: null,
    voterId: null,
    assigneeId: null,
    labelId: null,
  },
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
  // On phones the list grows page by page instead of showing one page at a time.
  continuous: false,
  loadingMore: false,
  loadMoreError: null,
});

const state = initialState();

const actions = {
  getOpportunitiesByTenders({ commit }, { tenderIds }) {
    const pageSize = 99999;

    return opportunityService.getOpportunitiesByTenders({ tenderIds, pageSize }).then(
      (opportunitiesByTenders) => {
        return opportunitiesByTenders.items;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  // gets opportunities and returns the response retrieved
  getOpportunities({ commit }, page = 1) {
    commit('GET_OPPORTUNITIES');
    const sortingField = state.sortingField;
    const sortingOrder = state.sortingOrder;
    const filters = state.filters;

    return opportunityService.getOpportunities({ page, sortingField, sortingOrder, filters }).then(
      (response) => {
        commit('GET_OPPORTUNITIES_SUCCESS');
        return response;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  // gets opportunities and saves them into state
  searchOpportunities({ commit }, page = 1) {
    actions.getOpportunities({ commit }, page).then((response) => {
      // A newer search cancels this one and `getOpportunities` then resolves with nothing: keep the current list.
      if (!response) return;
      const { opportunities, pagination } = response;
      updateRouteQuery('page', pagination.page);
      state.opportunities = opportunities;
      state.pagination = pagination;
    });
  },
  // Adds the next page under the listed ones. Not `pending`: the list stays usable while it
  // loads, and a failure keeps what's listed.
  loadMoreOpportunities({ commit, state }, page) {
    commit('LOAD_MORE_OPPORTUNITIES');
    const { sortingField, sortingOrder, filters } = state;
    return opportunityService.getOpportunities({ page, sortingField, sortingOrder, filters }).then(
      (response) => commit('APPEND_OPPORTUNITIES', response),
      (error) => {
        // A new search cancels it, and replaces the list anyway.
        if (axios.isCancel(error)) commit('LOAD_MORE_CANCELLED');
        else commit('LOAD_MORE_ERROR', error);
      },
    );
  },
  setContinuous({ commit }, continuous) {
    commit('SET_CONTINUOUS', continuous);
  },
  addOpportunity({ commit }, newOpportunity) {
    commit('ADD_OPPORTUNITY');
    const tenderId = newOpportunity.tenderId;
    // Creates an opportunity from a tenderId, or create an opportunity from scratch
    const payload = tenderId
      ? {
          name: newOpportunity.name,
          tenderId: tenderId,
          evaluationGridTemplateId: null,
          statusId: null,
          startDate: null,
          endDate: null,
          source: null,
          emitter: null,
          notes: null,
        }
      : newOpportunity;
    return opportunityService.addOpportunity(payload).then(
      (opportunity) => {
        if (tenderId) commit('tenders/SUCCESS_ADDING_OPPORTUNITY', { tenderId, opportunity }, { root: true });
        commit('ADD_OPPORTUNITY_SUCCESS', opportunity);
        return opportunity;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  // Adding opportunities in batch
  addOpportunities({ commit }, { tenderIds }) {
    commit('ADD_OPPORTUNITIES');
    return opportunityService.addOpportunities({ tenderIds }).then(
      (opportunities) => {
        commit('tenders/SUCCESS_ADDING_OPPORTUNITIES', opportunities, { root: true });
        commit('ADD_OPPORTUNITIES_SUCCESS', opportunities);
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  setCustomSortingOrder({ commit }, { sortingOrder }) {
    commit('SET_CUSTOM_SORTING_ORDER', sortingOrder);
  },
  setCustomSortingField({ commit }, { sortingField }) {
    commit('SET_CUSTOM_SORTING_FIELD', sortingField);
  },
  setHasDocumentsSearchFilter({ commit }, hasDocuments) {
    updateRouteQuery('hasDocuments', hasDocuments);
    commit('SET_HAS_DOCUMENTS', hasDocuments);
  },
  setOpportunityStatusSearchFilter({ commit }, opportunityStatusIds) {
    updateRouteQuery('opportunityStatusId', opportunityStatusIds);
    commit('SET_OPPORTUNITY_STATUS_ID', opportunityStatusIds);
  },
  setVoterSearchFilter({ commit }, voterId) {
    updateRouteQuery('voterId', voterId);
    commit('SET_VOTER_ID', voterId);
  },
  setAssigneeSearchFilter({ commit }, assigneeId) {
    updateRouteQuery('assigneeId', assigneeId);
    commit('SET_ASSIGNEE_ID', assigneeId);
  },
  setLabelSearchFilter({ commit }, labelIds) {
    updateRouteQuery('labelId', labelIds);
    commit('SET_LABEL_ID', labelIds);
  },
  setCreatorSearchFilter({ commit }, authorId) {
    updateRouteQuery('authorId', authorId);
    commit('SET_AUTHOR_ID', authorId);
  },
  setQuerySearchFilter({ commit }, { query }) {
    commit('SET_QUERY_SEARCH_FILTER', query);
  },
  deleteMany({ commit }, opportunities) {
    const opportunityIds = opportunities.map((opportunity) => opportunity.id);

    return opportunityService.deleteMany(opportunityIds).then(
      (response) => {
        const successOpportunityIds = response.success;

        if (successOpportunityIds && successOpportunityIds.length > 0) {
          commit('DELETE_OPPORTUNITIES_SUCCESS', successOpportunityIds);
          const tenderIds = opportunities
            .filter((opportunity) => successOpportunityIds.includes(opportunity.id) && opportunity.tenderId)
            .map((opportunity) => opportunity.tenderId);

          if (tenderIds.length > 0) commit('tenders/SUCCESS_DELETING_OPPORTUNITIES', tenderIds, { root: true });
        }
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR', error);
          throw error;
        }
      },
    );
  },
  updateOpportunitiesStatus({ commit }, { newOpportunityStatusId, filters }) {
    commit('UPDATING_OPPORTUNITIES');

    return opportunityService
      .updateStatusMany({
        newOpportunityStatusId,
        filters,
      })
      .then(
        () => {
          commit('UPDATING_OPPORTUNITIES_SUCCESS');
        },
        (error) => {
          if (!axios.isCancel(error)) {
            commit('ERROR', error);
            throw error;
          }
        },
      );
  },
  RESET({ commit }) {
    commit('RESET_OPPORTUNITIES');
  },
};

const mutations = {
  GET_OPPORTUNITIES(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.loadMoreError = null;
  },
  SET_CONTINUOUS(state, continuous) {
    state.continuous = continuous;
  },
  LOAD_MORE_OPPORTUNITIES(state) {
    state.loadingMore = true;
    state.loadMoreError = null;
  },
  APPEND_OPPORTUNITIES(state, { opportunities, pagination }) {
    // One created since the previous page shifts the pages: its last row comes back first.
    const listed = new Set(state.opportunities.map((opportunity) => opportunity.id));
    state.opportunities = [...state.opportunities, ...opportunities.filter(({ id }) => !listed.has(id))];
    state.pagination = pagination;
    state.loadingMore = false;
  },
  LOAD_MORE_ERROR(state, error) {
    state.loadingMore = false;
    state.loadMoreError = error;
  },
  LOAD_MORE_CANCELLED(state) {
    state.loadingMore = false;
  },
  UPDATING_OPPORTUNITIES(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  UPDATING_OPPORTUNITIES_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
      rejected: false,
    };
  },
  GET_OPPORTUNITIES_SUCCESS(state) {
    state.status = {
      ...state.status,
      resolved: true,
      pending: false,
    };
  },
  EDIT_OPPORTUNITY_SUCCESS(state, opportunity) {
    state.opportunities = state.opportunities.map((opp) => {
      if (opp.id !== opportunity.id) return opp;
      // `votes`: votes are managed by the go-nogo module and aren't part of an edit
      // so the edit response's votes must not clobber the existing votes display.
      return { ...opp, ...opportunity, votes: opp.votes };
    });
  },
  RESET_OPPORTUNITY(state) {
    state.opportunity = null;
  },
  ADD_OPPORTUNITY(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  ADD_OPPORTUNITY_SUCCESS(state, opportunity) {
    state.status = {
      ...state.status,
      resolved: true,
      pending: false,
    };
    state.opportunities.unshift(opportunity);
  },
  ADD_OPPORTUNITIES(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  ADD_OPPORTUNITIES_SUCCESS(state, opportunities) {
    state.status = {
      ...state.status,
      resolved: true,
      pending: false,
    };
    state.opportunities.unshift(...opportunities);
  },
  OPPORTUNITY_DELETE_SUCCESS(state, id) {
    state.status = {
      ...state.status,
      resolved: true,
      pending: false,
    };

    removeItemFromList(id, state.opportunities);
  },
  DELETE_OPPORTUNITIES_SUCCESS(state, ids) {
    state.status = {
      ...state.status,
      resolved: true,
      pending: false,
    };

    state.opportunities = removeItemsFromList(ids, state.opportunities);
  },
  ADD_OPPORTUNITY_VOTE(state, { opportunityId, opportunityVote }) {
    const opportunity = state.opportunities.find((opportunity) => opportunity.id === opportunityId);
    if (!opportunity) return;
    opportunity.votes.push(opportunityVote);
  },
  UPDATE_OPPORTUNITY_VOTE(state, { opportunityId, userId, vote }) {
    const opportunity = state.opportunities.find((opportunity) => opportunity.id === opportunityId);
    if (!opportunity) return;
    const userVote = opportunity.votes.find((vote) => vote.userId === userId);
    userVote.isPending = false;
    userVote.vote = vote;
  },
  UPDATE_ASSIGNEES(state, { opportunityId, assignees }) {
    const index = state.opportunities.findIndex((opportunity) => opportunity.id === opportunityId);
    if (index < 0) return;
    state.opportunities.splice(index, 1, { ...state.opportunities[index], assignees });
  },
  UPDATE_LABELS(state, { opportunityId, labels }) {
    const index = state.opportunities.findIndex((opportunity) => opportunity.id === opportunityId);
    if (index < 0) return;
    state.opportunities.splice(index, 1, { ...state.opportunities[index], labels });
  },
  SET_FINAL_DECISION(state, { opportunityId, finalDecision }) {
    const index = state.opportunities.findIndex((opportunity) => opportunity.id === opportunityId);
    if (index < 0) return;
    state.opportunities.splice(index, 1, { ...state.opportunities[index], finalDecision });
  },
  DELETE_OPPORTUNITY_VOTE_SUCCESS(state, { opportunityId, voteId }) {
    const opportunity = state.opportunities.find((opportunity) => opportunity.id === opportunityId);
    if (opportunity) removeItemFromList(voteId, opportunity.votes);
  },
  SUCCESS_DELETING_DOCUMENT(state, { opportunityId, documentId }) {
    const opportunity = state.opportunities.find((opp) => opp.id === opportunityId);
    if (opportunity) removeItemFromList(documentId, opportunity.documents);
  },
  SUCCESS_ADDING_DOCUMENTS(state, { opportunityId, documents }) {
    const opportunityIndex = state.opportunities.findIndex((opp) => opp.id === opportunityId);
    if (opportunityIndex < 0) return;
    const existing = state.opportunities[opportunityIndex].documents;
    const existingIds = new Set(existing.map((d) => d.id));
    const newDocs = documents.filter((d) => !existingIds.has(d.id));
    existing.push(...newDocs);
  },
  SET_CUSTOM_SORTING_ORDER(state, sortingOrder) {
    state.sortingOrder = sortingOrder;
  },
  SET_CUSTOM_SORTING_FIELD(state, sortingField) {
    state.sortingField = sortingField;
  },
  SET_HAS_DOCUMENTS(state, hasDocuments) {
    state.filters.hasDocuments = hasDocuments;
  },
  SET_OPPORTUNITY_STATUS_ID(state, opportunityStatusIds) {
    state.filters.opportunityStatusId = opportunityStatusIds;
  },
  SET_VOTER_ID(state, voterIds) {
    state.filters.voterId = voterIds;
  },
  SET_ASSIGNEE_ID(state, assigneeIds) {
    state.filters.assigneeId = assigneeIds;
  },
  SET_LABEL_ID(state, labelIds) {
    state.filters.labelId = labelIds;
  },
  SET_AUTHOR_ID(state, authorIds) {
    state.filters.authorId = authorIds;
  },
  SET_QUERY_SEARCH_FILTER(state, query) {
    state.filters.query = query;
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
    state.opportunities = [];
  },
  RESET_OPPORTUNITIES(state) {
    state.opportunities = [];
  },
};

const getters = {
  opportunities: (state) => state.opportunities,
  filters: (state) => state.filters,

  // Pagination
  page: (state) => state.pagination.page,
  totalCount: (state) => state.pagination.totalCount,
  totalPages: (state) => state.pagination.totalPages,
  continuous: (state) => state.continuous,
  loadingMore: (state) => state.loadingMore,
  loadMoreError: (state) => state.loadMoreError,
  sortingField: (state) => state.sortingField,
  sortingOrder: (state) => state.sortingOrder,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

const removeItemFromList = (id, list) => {
  if (!list) return;
  const index = list.findIndex((item) => item.id === id);
  if (index !== -1) list.splice(index, 1);
  return list;
};

const removeItemsFromList = (ids, list) => {
  if (!list) return;
  return list.filter((item) => !ids.includes(item.id));
};

const addItemsToList = (items, list) => {
  if (!list) return;
  list.push(...items);
  return list;
};

export const opportunities = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
